import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Menu, X } from "lucide-react";
import { useI18n } from "@/i18n/translations";
import { routes } from "@/data/site";
import { Logo } from "@/components/Logo";

const LangSwitch = ({ className = "" }: { className?: string }) => {
  const { lang, setLang } = useI18n();
  return (
    <div className={`flex items-center gap-1 text-sm font-semibold ${className}`} role="group" aria-label="Lingua / Language">
      <button
        type="button"
        data-testid="lang-toggle-it"
        onClick={() => setLang("it")}
        className={`px-2 py-1 transition-colors ${lang === "it" ? "text-bordeaux" : "text-mutedtx hover:text-ink"}`}
        aria-pressed={lang === "it"}
      >
        IT
      </button>
      <span className="text-borderc" aria-hidden="true">/</span>
      <button
        type="button"
        data-testid="lang-toggle-en"
        onClick={() => setLang("en")}
        className={`px-2 py-1 transition-colors ${lang === "en" ? "text-bordeaux" : "text-mutedtx hover:text-ink"}`}
        aria-pressed={lang === "en"}
      >
        EN
      </button>
    </div>
  );
};

const navLinkClass = (isActive: boolean) =>
  `link-underline text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors ${
    isActive ? "text-bordeaux" : "text-ink/80 hover:text-ink"
  }`;

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // Guardia frecce cronologia: disabilitate ai bordi della cronologia (evita di uscire dal sito).
  const [histIdx, setHistIdx] = useState(0);
  const maxIdx = useRef(0);
  useEffect(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    maxIdx.current = Math.max(maxIdx.current, idx);
    setHistIdx(idx);
  }, [location]);
  const canBack = histIdx > 0;
  const canForward = histIdx < maxIdx.current;

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-borderc bg-paper/90 backdrop-blur-md">
      <div className="container-x flex h-16 items-center justify-between gap-4">
        <Logo />
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Menu principale">
          {routes.map((r) => (
            <div key={r.path} className="group relative flex h-16 items-center">
              <NavLink to={r.path} end={r.path === "/"} data-testid={r.testid} className={({ isActive }) => navLinkClass(isActive)}>
                <MenuLabel k={r.key} />
              </NavLink>
              {r.children && (
                <div
                  className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 opacity-0 transition-all duration-200 focus-within:visible focus-within:opacity-100 group-hover:visible group-hover:opacity-100"
                  data-testid={`nav-dropdown-${r.testid}`}
                >
                  <div className="flex w-72 flex-col border border-borderc bg-paper py-2 shadow-[0_16px_40px_rgba(28,25,23,0.10)]">
                    {r.children.map((c) => (
                      <NavLink
                        key={c.path}
                        to={c.path}
                        data-testid={c.testid}
                        className={({ isActive }) =>
                          `px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                            isActive ? "bg-cardwhite text-bordeaux" : "text-ink/80 hover:bg-cardwhite hover:text-ink"
                          }`
                        }
                      >
                        <MenuLabel k={c.key} />
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1.5" role="group" aria-label="Cronologia di navigazione">
            <button
              type="button"
              data-testid="nav-history-back"
              aria-label="Torna alla pagina precedente"
              title="Torna alla pagina precedente"
              onClick={() => navigate(-1)}
              disabled={!canBack}
              className="inline-flex h-10 w-10 items-center justify-center border border-borderc text-ink transition-colors hover:text-bordeaux disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              data-testid="nav-history-forward"
              aria-label="Vai alla pagina successiva"
              title="Vai alla pagina successiva"
              onClick={() => navigate(1)}
              disabled={!canForward}
              className="inline-flex h-10 w-10 items-center justify-center border border-borderc text-ink transition-colors hover:text-bordeaux disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          <LangSwitch className="hidden sm:flex" />
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            className="inline-flex h-10 w-10 items-center justify-center border border-borderc text-ink lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="border-t border-borderc bg-paper lg:hidden"
            data-testid="nav-mobile-panel"
          >
            <nav className="container-x flex flex-col py-6" aria-label="Menu principale mobile">
              {routes.map((r) => (
                <div key={r.path}>
                  <NavLink
                    to={r.path}
                    end={r.path === "/"}
                    data-testid={r.testid}
                    className={({ isActive }) =>
                      `border-b border-borderc/70 block py-4 font-serif text-2xl transition-colors ${
                        isActive ? "italic text-bordeaux" : "text-ink"
                      }`
                    }
                  >
                    <MenuLabel k={r.key} />
                  </NavLink>
                  {r.children && (
                    <div className="flex flex-col border-b border-borderc/70 pb-3 pt-2" data-testid="nav-mobile-attivita-sub">
                      {r.children.map((c) => (
                        <NavLink
                          key={c.path}
                          to={c.path}
                          data-testid={c.testid.replace("nav-link-", "nav-mobile-link-")}
                          className={({ isActive }) =>
                            `py-2.5 pl-4 text-sm font-semibold uppercase tracking-[0.12em] transition-colors ${
                              isActive ? "text-bordeaux" : "text-mutedtx hover:text-ink"
                            }`
                          }
                        >
                          <MenuLabel k={c.key} />
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between pt-5">
                <LangSwitch />
                <span className="text-xs text-mutedtx">cspdlm.it</span>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const MenuLabel = ({ k }: { k: string }) => {
  const { t } = useI18n();
  return <>{t(k)}</>;
};
